import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { GoogleLinkCard } from "../components/GoogleLinkCard";
import { type GoogleIntegrationView } from "../lib/googleIntegration";
import { getGoogleConsentStatus } from "../lib/googleConsentStatus";
import {
  GOOGLE_SCOPE_BUNDLES,
  createDefaultGoogleScopeGrantedMap,
  getGoogleIntegrationAggregateStatusLabel,
  getGoogleScopeBundleLabel,
  isGoogleLinkMode,
  isGoogleScopeBundle,
  resolveGoogleIntegrationAggregateState,
  type GoogleLinkMode,
  type GoogleScopeBundle,
  type GoogleScopeGrantedMap,
} from "../lib/googleScopes";
import { UI_TEXT_TONE } from "../lib/uiTone";

type GoogleLinkRouteState = {
  returnTo?: string;
  returnState?: unknown;
  scopeBundle?: GoogleScopeBundle;
  mode?: GoogleLinkMode;
};

const GOOGLE_OAUTH_POPUP_NAME = "wizmate-google-oauth";
const GOOGLE_OAUTH_POPUP_FEATURES = "popup=yes,width=520,height=720";

const GOOGLE_LINK_SCOPE_OPTIONS = [
  {
    scopeBundle: GOOGLE_SCOPE_BUNDLES.bundle,
    title: "ファイル保存連携",
    description:
      "領収書などの画像・読み取り結果を、WizMateが作成するGoogle Driveファイルとスプレッドシートに保存します。",
    buttonLabel: "ファイル保存を連携",
    testId: "google-consent-bundle-button",
  },
  {
    scopeBundle: GOOGLE_SCOPE_BUNDLES.calendarToday,
    title: "カレンダー確認連携",
    description:
      "通常会話で予定を確認するときに、Googleカレンダーの予定を読み取って回答します。予定の作成・変更・削除はしません。",
    buttonLabel: "カレンダー確認を連携",
    testId: "google-consent-calendar-today-button",
  },
] as const;

export default function GoogleLinkPopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as GoogleLinkRouteState;

  const routeScopeBundle = routeState.scopeBundle;
  const queryScopeBundle = searchParams.get("scopeBundle");
  const parsedScopeBundle = isGoogleScopeBundle(routeScopeBundle)
    ? routeScopeBundle
    : isGoogleScopeBundle(queryScopeBundle)
      ? queryScopeBundle
      : GOOGLE_SCOPE_BUNDLES.bundle;

  const routeMode = routeState.mode;
  const queryMode = searchParams.get("mode");
  const parsedMode = isGoogleLinkMode(routeMode)
    ? routeMode
    : isGoogleLinkMode(queryMode)
      ? queryMode
      : "single";

  const returnTo = routeState.returnTo || searchParams.get("returnTo") || "/home";

  const [activeScopeBundle, setActiveScopeBundle] = useState<GoogleScopeBundle>(parsedScopeBundle);
  const [scopeGrantedMap, setScopeGrantedMap] = useState<GoogleScopeGrantedMap>(
    createDefaultGoogleScopeGrantedMap()
  );
  const [scopeStatusLoaded, setScopeStatusLoaded] = useState(false);
  const [allConsentMessage, setAllConsentMessage] = useState<string | null>(null);
  const [allConsentError, setAllConsentError] = useState<string | null>(null);
  const [autoStartToken, setAutoStartToken] = useState<number | undefined>(undefined);
  const preparedPopupRef = useRef<Window | null>(null);

  const closePopup = () => {
    navigate(returnTo, { replace: true, state: routeState.returnState });
  };

  const aggregateStatus = useMemo(
    () => resolveGoogleIntegrationAggregateState(scopeGrantedMap),
    [scopeGrantedMap]
  );
  const aggregateLabel = getGoogleIntegrationAggregateStatusLabel(aggregateStatus);
  const isSelectorMode = parsedMode === "selector" || parsedMode === "all";
  const isActiveScopeGranted =
    activeScopeBundle === GOOGLE_SCOPE_BUNDLES.receipt
      ? scopeGrantedMap[GOOGLE_SCOPE_BUNDLES.bundle]
      : scopeGrantedMap[activeScopeBundle];
  const shouldForceConnectedStatusForCard =
    isSelectorMode &&
    isActiveScopeGranted;
  const shouldForceShowDisconnectButtonForCard =
    isSelectorMode &&
    isActiveScopeGranted;

  const refreshScopeGrantedMap = useCallback(async () => {
    try {
      const consentStatus = await getGoogleConsentStatus();
      const nextMap = createDefaultGoogleScopeGrantedMap();
      nextMap[GOOGLE_SCOPE_BUNDLES.bundle] =
        consentStatus.bundle.granted || consentStatus.receipt.granted;
      nextMap[GOOGLE_SCOPE_BUNDLES.calendarToday] = consentStatus.calendarToday.granted;

      setScopeGrantedMap(nextMap);
      return nextMap;
    } catch {
      const fallbackMap = createDefaultGoogleScopeGrantedMap();
      setScopeGrantedMap(fallbackMap);
      return fallbackMap;
    } finally {
      setScopeStatusLoaded(true);
    }
  }, []);

  const triggerAutoStart = useCallback(() => {
    setAutoStartToken((prev) => (typeof prev === "number" ? prev + 1 : 1));
  }, []);

  const preopenGoogleOAuthPopup = useCallback(() => {
    const popup = window.open("", GOOGLE_OAUTH_POPUP_NAME, GOOGLE_OAUTH_POPUP_FEATURES);
    preparedPopupRef.current = popup;
  }, []);

  const consumePreparedPopup = useCallback(() => {
    const popup = preparedPopupRef.current;
    preparedPopupRef.current = null;
    return popup;
  }, []);

  const closePreparedPopup = useCallback(() => {
    const popup = preparedPopupRef.current;
    preparedPopupRef.current = null;
    if (popup && !popup.closed && typeof popup.close === "function") {
      popup.close();
    }
  }, []);

  const startScopeConsent = useCallback(async (scopeBundle: GoogleScopeBundle) => {
    preopenGoogleOAuthPopup();
    setAllConsentMessage(null);
    setAllConsentError(null);

    const latestMap = await refreshScopeGrantedMap();
    const targetGranted =
      scopeBundle === GOOGLE_SCOPE_BUNDLES.receipt
        ? latestMap[GOOGLE_SCOPE_BUNDLES.bundle]
        : latestMap[scopeBundle];

    if (targetGranted) {
      closePreparedPopup();
      setAllConsentMessage(`${getGoogleScopeBundleLabel(scopeBundle)}は連携済みです。`);
      return;
    }

    setActiveScopeBundle(scopeBundle);
    triggerAutoStart();
  }, [closePreparedPopup, preopenGoogleOAuthPopup, refreshScopeGrantedMap, triggerAutoStart]);

  const handleScopeStatusChange = useCallback(
    (scopeBundle: GoogleScopeBundle, status: GoogleIntegrationView) => {
      const targetScopeBundle =
        scopeBundle === GOOGLE_SCOPE_BUNDLES.receipt
          ? GOOGLE_SCOPE_BUNDLES.bundle
          : scopeBundle;
      setScopeGrantedMap((prev) => ({
        ...prev,
        [targetScopeBundle]: status.state === "connected",
      }));
    },
    []
  );

  const handleScopeConnected = useCallback(
    async (scopeBundle: GoogleScopeBundle) => {
      const targetScopeBundle =
        scopeBundle === GOOGLE_SCOPE_BUNDLES.receipt
          ? GOOGLE_SCOPE_BUNDLES.bundle
          : scopeBundle;

      setScopeGrantedMap((prev) => ({
        ...prev,
        [targetScopeBundle]: true,
      }));
      setAllConsentMessage(`${getGoogleScopeBundleLabel(scopeBundle)}が完了しました。`);
      void refreshScopeGrantedMap();
    },
    [refreshScopeGrantedMap]
  );

  const handleScopeInterrupted = useCallback(
    (scopeBundle: GoogleScopeBundle) => {
      setAllConsentError(
        `「${getGoogleScopeBundleLabel(scopeBundle)}」の連携が中断されました。状態を確認して再試行してください。`
      );
      void refreshScopeGrantedMap();
    },
    [refreshScopeGrantedMap]
  );

  const handleScopeDisconnected = useCallback(() => {
    setScopeGrantedMap(createDefaultGoogleScopeGrantedMap());
  }, []);

  useEffect(() => {
    void refreshScopeGrantedMap();
  }, [refreshScopeGrantedMap]);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-xl items-center justify-center">
        <div className="relative w-full max-w-full rounded-[2.4rem] border border-white/80 bg-white/90 p-4 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <button
            type="button"
            onClick={closePopup}
            aria-label="閉じる"
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EADBC8] bg-[#FFF8F4] text-[#7C5A4E] transition hover:bg-white"
          >
            ×
          </button>

          <div className="mb-5 pr-12">
            <h1 className={`text-2xl font-black tracking-tight ${UI_TEXT_TONE.title}`}>Google連携</h1>
            <p className={`mt-2 text-sm leading-6 ${UI_TEXT_TONE.body}`}>
              Google権限を連携して、ファイル保存やカレンダー確認を使えるようにします。
            </p>
          </div>

          {isSelectorMode ? (
            <div className="mb-5 rounded-[1.6rem] border border-[#EADBC8] bg-[#FFF8F4] px-4 py-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-black text-[#7C5A4E]">連携ステータス</p>
                <span className="rounded-full border border-[#E6DACD] bg-white px-3 py-1 text-xs font-black text-[#A46D4C]">
                  {aggregateLabel}
                </span>
              </div>

              <div className="mt-4 space-y-2 text-sm">
                {GOOGLE_LINK_SCOPE_OPTIONS.map((option) => (
                  <div
                    key={option.scopeBundle}
                    className="rounded-xl border border-[#F1E3D8] bg-white px-3 py-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-black text-[#5D4D46]">{option.title}</p>
                        <p className="mt-1 text-xs leading-5 text-[#7B6861]">{option.description}</p>
                      </div>
                      <span className="shrink-0 whitespace-nowrap text-xs font-bold text-[#7B6861]">
                        {scopeGrantedMap[option.scopeBundle] ? "連携済み" : "未連携"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 grid grid-cols-1 gap-2">
                {GOOGLE_LINK_SCOPE_OPTIONS.map((option) => (
                  <button
                    key={option.scopeBundle}
                    type="button"
                    data-testid={option.testId}
                    onClick={() => void startScopeConsent(option.scopeBundle)}
                    disabled={!scopeStatusLoaded || scopeGrantedMap[option.scopeBundle]}
                    className="rounded-full bg-[#B07A55] px-3 py-2 text-xs font-black text-white transition hover:bg-[#986648] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {option.buttonLabel}
                  </button>
                ))}
              </div>

              {allConsentMessage ? (
                <p className="mt-3 rounded-xl border border-[#D8E7D3] bg-[#F7FBF3] px-3 py-2 text-xs font-bold text-[#58705A]">
                  {allConsentMessage}
                </p>
              ) : null}
              {allConsentError ? (
                <p className="mt-3 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-3 py-2 text-xs font-bold text-[#A1493E]">
                  {allConsentError}
                </p>
              ) : null}
            </div>
          ) : null}

          <GoogleLinkCard
            scopeBundle={activeScopeBundle}
            autoStartToken={autoStartToken}
            showServiceSummary={!isSelectorMode}
            showConnectButton={!isSelectorMode}
            forceConnectedStatus={shouldForceConnectedStatusForCard}
            forceShowDisconnectButton={shouldForceShowDisconnectButtonForCard}
            consumePreparedPopup={consumePreparedPopup}
            onStatusChange={handleScopeStatusChange}
            onConnected={handleScopeConnected}
            onDisconnected={handleScopeDisconnected}
            onInterrupted={handleScopeInterrupted}
          />
        </div>
      </div>
    </main>
  );
}
